import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { updateCompany, writeCompanyAudit, getFeatureGroups, getFeatureKeys } from "@/lib/firebase";
import { Loader2, Building2, Key } from "lucide-react";
import { logger } from "@/lib/logger";
import { useAuth } from "@/contexts/use-auth";

interface FeatureGroup {
  id: string;
  name: string;
}

interface FeatureKey {
  id: string;
  name: string;
  keyValue: string;
  description: string;
  enabled: boolean;
  featureGroup?: string;
  displayOrder?: number;
}

interface Company {
  id: string;
  name: string;
  type?: string;
  status: string;
  featureKeys?: string[];
  features?: Array<{ featureGroup: string; featureKeys: string[] }>;
}

interface EditCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: Company;
  onSaved?: (company: Company) => void;
}

export default function EditCustomerModal({ isOpen, onClose, company, onSaved }: EditCustomerModalProps) {
  const [companyName, setCompanyName] = useState(company.name || "");
  const [companyType, setCompanyType] = useState(company.type || "Customer");
  const [companyStatus, setCompanyStatus] = useState(company.status || "newCompany");
  const [featureGroups, setFeatureGroups] = useState<FeatureGroup[]>([]);
  const [featureKeys, setFeatureKeys] = useState<FeatureKey[]>([]);
  const [featureToggles, setFeatureToggles] = useState<Set<string>>(new Set());
  const [loadingFeatureKeys, setLoadingFeatureKeys] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen) {
      setCompanyName(company.name || "");
      setCompanyType(company.type || "Customer");
      setCompanyStatus(company.status || "newCompany");
      loadFeatureData();
    }
  }, [isOpen, company]);

  const loadFeatureData = async () => {
    setLoadingFeatureKeys(true);
    try {
      const [groupsArr, keysArr] = await Promise.all([
        getFeatureGroups(),
        getFeatureKeys(),
      ]);

      setFeatureGroups(groupsArr as FeatureGroup[]);

      const keys = (keysArr as FeatureKey[])
        .map(d => {
          const name: string = d.name || '';
          return { ...d, keyValue: d.keyValue || name.trim().toLowerCase().replace(/\s+/g, '') } as FeatureKey;
        })
        .filter(k => k.enabled)
        .sort((a, b) => {
          const aO = a.displayOrder ?? Infinity;
          const bO = b.displayOrder ?? Infinity;
          if (aO !== bO) return aO - bO;
          return a.name.localeCompare(b.name);
        });
      setFeatureKeys(keys);

      // Initialise toggles from company data
      let toggleIds = new Set<string>();

      if (company.features && company.features.length > 0) {
        // New format: use featureKeys doc IDs directly
        toggleIds = new Set(company.features.flatMap(fg => fg.featureKeys));
      } else if (Array.isArray(company.featureKeys) && company.featureKeys.length > 0) {
        // Old format: company.featureKeys is keyValue strings — map to doc IDs
        const kvToId = new Map(keys.map(k => [k.keyValue, k.id]));
        for (const kv of company.featureKeys) {
          const id = kvToId.get(kv);
          if (id) toggleIds.add(id);
        }
      }

      setFeatureToggles(toggleIds);
    } catch (err: any) {
      logger.error("Error loading feature data:", err);
    } finally {
      setLoadingFeatureKeys(false);
    }
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!companyName.trim()) errs.companyName = "Company name is required";
    if (!companyType) errs.companyType = "Type is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildFeaturesArray = (): Array<{ featureGroup: string; featureKeys: string[] }> => {
    const groupMap = new Map<string, string[]>();
    const ungrouped: string[] = [];

    for (const fkId of Array.from(featureToggles)) {
      const fk = featureKeys.find(k => k.id === fkId);
      if (fk?.featureGroup) {
        const arr = groupMap.get(fk.featureGroup) || [];
        arr.push(fkId);
        groupMap.set(fk.featureGroup, arr);
      } else {
        ungrouped.push(fkId);
      }
    }

    const result = Array.from(groupMap.entries()).map(([featureGroup, fkIds]) => ({
      featureGroup,
      featureKeys: fkIds,
    }));

    if (ungrouped.length > 0) {
      result.push({ featureGroup: '', featureKeys: ungrouped });
    }

    return result;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const features = buildFeaturesArray();

      await updateCompany(company.id, {
        name: companyName.trim(),
        type: companyType,
        status: companyStatus,
        features,
      });

      // The update has succeeded, so let the parent replace its stale list row
      // immediately rather than waiting for the 15-second polling refresh.
      onSaved?.({
        ...company,
        name: companyName.trim(),
        type: companyType,
        status: companyStatus,
        features,
      });

      // Build audit log changes
      const changes: { field: string; from: string; to: string }[] = [];
      if (companyName.trim() !== company.name) {
        changes.push({ field: 'name', from: company.name || '', to: companyName.trim() });
      }
      if (companyType !== (company.type || 'Customer')) {
        changes.push({ field: 'type', from: company.type || 'Customer', to: companyType });
      }
      if (companyStatus !== (company.status || 'newCompany')) {
        changes.push({ field: 'status', from: company.status || 'newCompany', to: companyStatus });
      }

      // Diff features
      const oldToggleIds = new Set<string>();
      if (company.features && company.features.length > 0) {
        for (const fg of company.features) for (const id of fg.featureKeys) oldToggleIds.add(id);
      } else if (Array.isArray(company.featureKeys)) {
        const kvToId = new Map(featureKeys.map(k => [k.keyValue, k.id]));
        for (const kv of company.featureKeys) {
          const id = kvToId.get(kv);
          if (id) oldToggleIds.add(id);
        }
      }

      const idToName = new Map(featureKeys.map(k => [k.id, k.name]));
      const oldNames = Array.from(oldToggleIds).map(id => idToName.get(id) ?? id).sort().join(', ');
      const newNames = Array.from(featureToggles).map(id => idToName.get(id) ?? id).sort().join(', ');
      if (oldNames !== newNames) {
        changes.push({ field: 'features', from: oldNames || 'none', to: newNames || 'none' });
      }

      await writeCompanyAudit(company.id, companyName.trim(), 'updated', user?.email ?? 'unknown', user?.uid ?? '', changes);

      toast({
        title: "Customer updated",
        description: `${companyName.trim()} has been updated successfully.`,
      });
      onClose();
    } catch (error: any) {
      logger.error("❌ Error updating customer:", error);
      toast({
        title: "Failed to update customer",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const toggleFeature = (fkId: string, val: boolean) => {
    setFeatureToggles(prev => {
      const next = new Set(prev);
      if (val) next.add(fkId);
      else next.delete(fkId);
      return next;
    });
  };

  const statusOptions = [
    "newCompany",
    "active",
    "inactive",
    "pending",
    "suspended",
  ];

  const ungroupedKeys = featureKeys.filter(fk => !fk.featureGroup || !featureGroups.find(g => g.id === fk.featureGroup));
  const hasAnyFeatureKeys = featureKeys.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Edit Customer — {company.name}
          </DialogTitle>
          <DialogDescription>
            Update company details and feature access.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ec-name">
              Company Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="ec-name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Enter company name"
              disabled={isSubmitting}
              className={errors.companyName ? "border-red-500" : ""}
            />
            {errors.companyName && (
              <p className="text-sm text-red-600">{errors.companyName}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="ec-type">
              Type <span className="text-red-500">*</span>
            </Label>
            <Select value={companyType} onValueChange={setCompanyType} disabled={isSubmitting}>
              <SelectTrigger id="ec-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Customer">Customer</SelectItem>
                <SelectItem value="Partner">Partner</SelectItem>
              </SelectContent>
            </Select>
            {errors.companyType && (
              <p className="text-sm text-red-600">{errors.companyType}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="ec-status">Status</Label>
            <Select value={companyStatus} onValueChange={setCompanyStatus} disabled={isSubmitting}>
              <SelectTrigger id="ec-status">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(s => (
                  <SelectItem key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Features — grouped by feature group */}
          {loadingFeatureKeys ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-2 border-t pt-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading features...
            </div>
          ) : hasAnyFeatureKeys && (
            <div className="space-y-3 border-t pt-4">
              <Label className="flex items-center gap-1.5 text-sm font-medium">
                <Key className="h-4 w-4" />
                Features
              </Label>
              <p className="text-xs text-slate-500">Enable or disable features for this company.</p>

              {featureGroups.map(group => {
                const groupKeys = featureKeys.filter(fk => fk.featureGroup === group.id);
                if (groupKeys.length === 0) return null;
                return (
                  <div key={group.id} className="space-y-1">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{group.name}</p>
                    <div className="space-y-1 pl-2 border-l-2 border-slate-100">
                      {groupKeys.map(fk => (
                        <div key={fk.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700">{fk.name}</p>
                            {fk.description && <p className="text-xs text-slate-500 truncate">{fk.description}</p>}
                          </div>
                          <Switch
                            checked={featureToggles.has(fk.id)}
                            onCheckedChange={(val) => toggleFeature(fk.id, val)}
                            disabled={isSubmitting}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {ungroupedKeys.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Other</p>
                  <div className="space-y-1 pl-2 border-l-2 border-slate-100">
                    {ungroupedKeys.map(fk => (
                      <div key={fk.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700">{fk.name}</p>
                          {fk.description && <p className="text-xs text-slate-500 truncate">{fk.description}</p>}
                        </div>
                        <Switch
                          checked={featureToggles.has(fk.id)}
                          onCheckedChange={(val) => toggleFeature(fk.id, val)}
                          disabled={isSubmitting}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
